import React, { useState } from "react";
import { apiGet, apiPost } from "../services/api";
import Button from "../components/common/Button";
import { useI18n } from "../i18n/I18nProvider";
import "./PatientPortal.css";

type PortalVisit = {
  visit_id: number;
  visit_date?: string;
  appointment_time?: string | null;
  status?: string;
  doctor_name?: string | null;
  doctor_username?: string | null;
  remarks?: string | null;
};

type PortalResponse = {
  patient?: { patient_id?: string; name?: string; mobile?: string };
  visits?: PortalVisit[];
};

export default function PatientPortal() {
  const { t } = useI18n();
  const [lookupMobile, setLookupMobile] = useState("");
  const [lookupName, setLookupName] = useState("");
  const [portalData, setPortalData] = useState<PortalResponse | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");

  const [reqName, setReqName] = useState("");
  const [reqMobile, setReqMobile] = useState("");
  const [reqAge, setReqAge] = useState("");
  const [reqGender, setReqGender] = useState("");
  const [reqDate, setReqDate] = useState("");
  const [reqTime, setReqTime] = useState("");
  const [reqDoctor, setReqDoctor] = useState("");
  const [reqNotes, setReqNotes] = useState("");
  const [reqLoading, setReqLoading] = useState(false);
  const [reqStatus, setReqStatus] = useState("");

  const handleLookup = async () => {
    setLookupError("");
    setPortalData(null);
    if (!lookupMobile.trim() || !lookupName.trim()) {
      setLookupError(t("portal_error_missing"));
      return;
    }
    setLookupLoading(true);
    try {
      const res = (await apiGet(
        `/api/public/portal?mobile=${encodeURIComponent(lookupMobile)}&name=${encodeURIComponent(lookupName)}`
      )) as PortalResponse;
      setPortalData(res || null);
    } catch (e: unknown) {
      setPortalData(null);
      setLookupError(t("portal_error_lookup"));
    } finally {
      setLookupLoading(false);
    }
  };

  const handleRequest = async () => {
    setReqStatus("");
    if (!reqName.trim() || !reqMobile.trim()) {
      setReqStatus(t("portal_error_missing"));
      return;
    }
    setReqLoading(true);
    try {
      await apiPost("/api/public/appointments", {
        name: reqName.trim(),
        mobile: reqMobile.trim(),
        age: reqAge ? Number(reqAge) : undefined,
        gender: reqGender || undefined,
        preferred_date: reqDate || undefined,
        preferred_time: reqTime || undefined,
        preferred_doctor: reqDoctor || undefined,
        notes: reqNotes || undefined,
      });
      setReqStatus(t("portal_request_success"));
      setReqNotes("");
    } catch (e: unknown) {
      setReqStatus(t("portal_request_fail"));
    } finally {
      setReqLoading(false);
    }
  };

  return (
    <div className="portal-page">
      <header className="portal-hero">
        <div>
          <h1>{t("portal_title")}</h1>
          <p>{t("portal_subtitle")}</p>
        </div>
      </header>

      <main className="portal-grid">
        <section className="portal-card">
          <h2>{t("portal_find_title")}</h2>
          <div className="portal-form">
            <input
              type="tel"
              placeholder={t("portal_mobile_placeholder")}
              value={lookupMobile}
              onChange={(e) => setLookupMobile(e.target.value)}
            />
            <input
              type="text"
              placeholder={t("portal_name_placeholder")}
              value={lookupName}
              onChange={(e) => setLookupName(e.target.value)}
            />
            <Button variant="primary" onClick={handleLookup} isLoading={lookupLoading}>
              {t("portal_view_btn")}
            </Button>
            {lookupError && <div className="portal-error">{lookupError}</div>}
          </div>

          {portalData?.patient && (
            <div className="portal-results">
              <div className="portal-patient">
                <strong>{portalData.patient.name || "Patient"}</strong>
                <span>#{portalData.patient.patient_id}</span>
              </div>
              {(portalData.visits || []).length === 0 ? (
                <div className="portal-empty">{t("portal_empty")}</div>
              ) : (
                <div className="portal-visit-list">
                  {(portalData.visits || []).map((v) => (
                    <div key={v.visit_id} className="portal-visit-item">
                      <div>
                        <div className="portal-visit-date">
                          {v.visit_date} {v.appointment_time ? `• ${new Date(v.appointment_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
                        </div>
                        <div className="portal-visit-meta">
                          {v.doctor_name || v.doctor_username || "Doctor"} • {v.status || "Scheduled"}
                        </div>
                        {v.remarks && <div className="portal-visit-remarks">{v.remarks}</div>}
                      </div>
                      <span className="portal-status">{v.status || t("portal_status_open")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        <section className="portal-card">
          <h2>{t("portal_request_title")}</h2>
          <div className="portal-form">
            <input
              type="text"
              placeholder={t("portal_name_placeholder")}
              value={reqName}
              onChange={(e) => setReqName(e.target.value)}
            />
            <input
              type="tel"
              placeholder={t("portal_mobile_placeholder")}
              value={reqMobile}
              onChange={(e) => setReqMobile(e.target.value)}
            />
            <div className="portal-row">
              <input
                type="number"
                placeholder={t("portal_age_placeholder")}
                value={reqAge}
                onChange={(e) => setReqAge(e.target.value)}
              />
              <select value={reqGender} onChange={(e) => setReqGender(e.target.value)}>
                <option value="">{t("portal_gender_placeholder")}</option>
                <option value="Male">{t("portal_gender_male")}</option>
                <option value="Female">{t("portal_gender_female")}</option>
                <option value="Other">{t("portal_gender_other")}</option>
              </select>
            </div>
            <div className="portal-row">
              <input type="date" value={reqDate} onChange={(e) => setReqDate(e.target.value)} />
              <input type="time" value={reqTime} onChange={(e) => setReqTime(e.target.value)} />
            </div>
            <input
              type="text"
              placeholder={t("portal_doctor_placeholder")}
              value={reqDoctor}
              onChange={(e) => setReqDoctor(e.target.value)}
            />
            <textarea
              placeholder={t("portal_notes_placeholder")}
              value={reqNotes}
              onChange={(e) => setReqNotes(e.target.value)}
            />
            <Button variant="outline" onClick={handleRequest} isLoading={reqLoading}>
              {t("portal_request_btn")}
            </Button>
            {reqStatus && <div className="portal-status-text">{reqStatus}</div>}
          </div>
        </section>
      </main>
    </div>
  );
}
